import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

/**
 * ThemeContext — zentrale Theme-/Dark-Mode-Steuerung der App.
 *
 * Funktionsweise (Achse 1 — Hell/Dunkel):
 *  - `theme` ist die NUTZER-Präferenz: 'light' | 'dark' | 'system'.
 *  - `resolvedTheme` ist das tatsächlich angezeigte Theme ('light' | 'dark'),
 *    bei 'system' abgeleitet aus `prefers-color-scheme`.
 *  - Bei 'dark' wird die Klasse `dark` an <html> gesetzt → die `.dark`-Tokens
 *    in index.css greifen. Komponenten müssen Tokens nutzen (bg-surface,
 *    text-foreground …), damit der Umschalter sichtbar wirkt.
 *  - Präferenz wird in localStorage (`STORAGE_KEY`) persistiert; bei 'system'
 *    reagiert die App live auf Änderungen der Systemeinstellung.
 *
 * Achse 2 — Kontrast (Barrierefreiheit), ORTHOGONAL zu Hell/Dunkel:
 *  - `contrast` ist die Nutzer-Präferenz: 'normal' | 'more' | 'system'.
 *  - `resolvedContrast` ('normal' | 'more') leitet sich bei 'system' aus
 *    `prefers-contrast: more` ab.
 *  - Bei 'more' wird die Klasse `contrast-more` an <html> gesetzt → die
 *    `.contrast-more`-Tokens greifen (kombiniert sich mit `.dark`).
 *  - Eigener localStorage-Key (`CONTRAST_STORAGE_KEY`), gleiche System-Live-Logik.
 *
 * Achse 3 — Stil / Trend-Theme, ORTHOGONAL zu Hell/Dunkel & Kontrast:
 *  - `style` ist die Nutzer-Präferenz: 'default' | 'neobrutalism' (erweiterbar).
 *  - Pro Stil eine CSS-Klasse an <html> (`STYLE_CLASSES`); 'default' = keine Klasse.
 *    Bei 'neobrutalism' → `theme-neobrutalism` → die `.theme-neobrutalism`-Tokens
 *    (Radius 0, harte Schatten, kräftige Kanten) greifen, frei kombinierbar mit
 *    `.dark` (`.dark.theme-neobrutalism`).
 *  - Kein 'system'-Default (es gibt keine System-Präferenz für den Stil) →
 *    Fallback 'default'. Eigener localStorage-Key (`STYLE_STORAGE_KEY`).
 *  - Neue Trend-Themes (z.B. 'liquid-glass') = ein Eintrag in SELECTABLE_STYLES +
 *    STYLE_CLASSES plus ein `.theme-*`-Block in index.css. Keine Komponentenänderung.
 *  - WÄHREND DER BETA: `SELECTABLE_STYLES` enthält nur 'default'. Die Achse und alle
 *    Neobrutalism-Tokens bleiben erhalten, nur wählen kann man den Stil nicht mehr
 *    (siehe Kommentar an der Konstante).
 */

const STORAGE_KEY = 'eachotter-theme';
const THEMES = ['light', 'dark', 'system'];

const CONTRAST_STORAGE_KEY = 'eachotter-contrast';
const CONTRASTS = ['normal', 'more', 'system'];

const STYLE_STORAGE_KEY = 'eachotter-style';
// Die Stile, die ein Nutzer tatsächlich wählen darf. Seit September 2026
// (Beta-Freigabe) nur noch 'default': Der Umschalter ist aus den Einstellungen
// ausgebaut, weil Neobrutalism in einer Schulklasse nur für Verwirrung sorgt.
//
// Diese Liste ist bewusst die Quelle für `readStored` und `setStyle` — damit fällt
// eine früher gespeicherte 'neobrutalism'-Wahl beim nächsten Start automatisch auf
// 'default' zurück und wird überschrieben. Sonst säße jeder, der den Umschalter
// einmal angefasst hat, ohne Rückweg in den scharfen Ecken fest.
//
// Nach der Beta wieder freigeben: 'neobrutalism' hier ergänzen und die Karte
// „Design-Stil" in pages/AppearanceSettingsPage.js zurückholen.
const SELECTABLE_STYLES = ['default'];
// CSS-Klasse je Stil ('default' = keine Klasse). Erweiterbar ohne Komponenten-Eingriff.
const STYLE_CLASSES = { neobrutalism: 'theme-neobrutalism' };

const ThemeContext = createContext(null);

const matchesMedia = (query) =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(query).matches;

const getSystemPrefersDark = () => matchesMedia('(prefers-color-scheme: dark)');
const getSystemPrefersMoreContrast = () => matchesMedia('(prefers-contrast: more)');

const readStored = (key, allowed, fallback = 'system') => {
  try {
    const stored = localStorage.getItem(key);
    if (stored && allowed.includes(stored)) return stored;
  } catch {
    // localStorage nicht verfügbar (Privatmodus o.ä.) → Fallback
  }
  return fallback;
};

const resolveTheme = (theme) =>
  theme === 'system' ? (getSystemPrefersDark() ? 'dark' : 'light') : theme;

const resolveContrast = (contrast) =>
  contrast === 'system' ? (getSystemPrefersMoreContrast() ? 'more' : 'normal') : contrast;

const applyThemeClass = (resolved) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
};

const applyContrastClass = (resolved) => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('contrast-more', resolved === 'more');
};

const applyStyleClass = (style) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  // Alle bekannten Stil-Klassen entfernen, dann die aktive setzen ('default' = keine).
  Object.values(STYLE_CLASSES).forEach((cls) => root.classList.remove(cls));
  const cls = STYLE_CLASSES[style];
  if (cls) root.classList.add(cls);
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => readStored(STORAGE_KEY, THEMES));
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(theme));

  const [contrast, setContrastState] = useState(() => readStored(CONTRAST_STORAGE_KEY, CONTRASTS));
  const [resolvedContrast, setResolvedContrast] = useState(() => resolveContrast(contrast));

  const [style, setStyleState] = useState(() => readStored(STYLE_STORAGE_KEY, SELECTABLE_STYLES, 'default'));

  // Theme anwenden + persistieren, sobald sich die Präferenz ändert.
  useEffect(() => {
    const resolved = resolveTheme(theme);
    setResolvedTheme(resolved);
    applyThemeClass(resolved);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Persistenz best-effort
    }
  }, [theme]);

  // Kontrast anwenden + persistieren, sobald sich die Präferenz ändert.
  useEffect(() => {
    const resolved = resolveContrast(contrast);
    setResolvedContrast(resolved);
    applyContrastClass(resolved);
    try {
      localStorage.setItem(CONTRAST_STORAGE_KEY, contrast);
    } catch {
      // Persistenz best-effort
    }
  }, [contrast]);

  // Stil anwenden + persistieren, sobald sich die Präferenz ändert.
  useEffect(() => {
    applyStyleClass(style);
    try {
      localStorage.setItem(STYLE_STORAGE_KEY, style);
    } catch {
      // Persistenz best-effort
    }
  }, [style]);

  // Bei 'system': live auf Änderungen der Systemeinstellung reagieren (Hell/Dunkel).
  useEffect(() => {
    if (theme !== 'system') return undefined;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const resolved = getSystemPrefersDark() ? 'dark' : 'light';
      setResolvedTheme(resolved);
      applyThemeClass(resolved);
    };
    // Safari < 14 kennt nur addListener
    if (mql.addEventListener) mql.addEventListener('change', handleChange);
    else mql.addListener(handleChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handleChange);
      else mql.removeListener(handleChange);
    };
  }, [theme]);

  // Bei 'system': live auf Änderungen der Systemeinstellung reagieren (Kontrast).
  useEffect(() => {
    if (contrast !== 'system') return undefined;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mql = window.matchMedia('(prefers-contrast: more)');
    const handleChange = () => {
      const resolved = getSystemPrefersMoreContrast() ? 'more' : 'normal';
      setResolvedContrast(resolved);
      applyContrastClass(resolved);
    };
    if (mql.addEventListener) mql.addEventListener('change', handleChange);
    else mql.addListener(handleChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handleChange);
      else mql.removeListener(handleChange);
    };
  }, [contrast]);

  const setTheme = useCallback((next) => {
    setThemeState(THEMES.includes(next) ? next : 'system');
  }, []);

  const setContrast = useCallback((next) => {
    setContrastState(CONTRASTS.includes(next) ? next : 'system');
  }, []);

  const setStyle = useCallback((next) => {
    setStyleState(SELECTABLE_STYLES.includes(next) ? next : 'default');
  }, []);

  // Einfacher Umschalter Hell ↔ Dunkel (auf Basis des aktuell Sichtbaren).
  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const visible = resolveTheme(current);
      return visible === 'dark' ? 'light' : 'dark';
    });
  }, []);

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      isDark: resolvedTheme === 'dark',
      setTheme,
      toggleTheme,
      themes: THEMES,
      contrast,
      resolvedContrast,
      isHighContrast: resolvedContrast === 'more',
      setContrast,
      contrasts: CONTRASTS,
      style,
      isNeobrutalism: style === 'neobrutalism',
      setStyle,
      styles: SELECTABLE_STYLES,
    }),
    [theme, resolvedTheme, setTheme, toggleTheme, contrast, resolvedContrast, setContrast, style, setStyle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme muss innerhalb von <ThemeProvider> genutzt werden');
  }
  return ctx;
};

export default ThemeContext;
